/**
 * THEME SLICE — Stato del brand del tenant scaricato dal backend (opzione C).
 *
 * Config-slice (non una lista): tiene la palette corrente + lo status del
 * fetch. `palette` null = non ancora caricata o tenant senza brand custom
 * (il ThemeManager applica DEFAULT_PALETTE in quel caso).
 */

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { themeApi } from './themeApi';
import { extractError } from './store/thunkHelpers';
import type { BrandPalette, BrandSeed } from './brandPalette';

/** Scarica la palette brand del tenant loggato (GET /company/settings). */
export const fetchTheme = createAsyncThunk<BrandPalette | null, void, { rejectValue: string }>(
  'theme/fetch',
  async (_, { rejectWithValue }) => {
    try {
      return await themeApi.getTheme();
    } catch (err) {
      return rejectWithValue(extractError(err));
    }
  },
);

/**
 * Salva i due colori scelti in Impostazioni → Azienda (PATCH /company/settings/palette).
 * Il reducer scrive la palette tornata dal backend in store: `ThemeManager` la
 * riapplica come CSS var e la mette in cache, quindi il cambio si vede subito
 * senza ricaricare la pagina.
 */
export const updateCompanyPalette = createAsyncThunk<BrandPalette, BrandSeed, { rejectValue: string }>(
  'theme/updatePalette',
  async (seed, { rejectWithValue }) => {
    try {
      return await themeApi.updatePalette(seed);
    } catch (err) {
      return rejectWithValue(extractError(err));
    }
  },
);

/**
 * Ripristina i colori della company (DELETE /company/settings/palette): il tema torna ai
 * colori predefiniti, che sono quelli del client. Il thunk restituisce la palette
 * da applicare, non un vuoto, così il reducer e i componenti trattano ripristino e
 * salvataggio allo stesso modo.
 */
export const resetCompanyPalette = createAsyncThunk<BrandPalette, void, { rejectValue: string }>(
  'theme/resetPalette',
  async (_, { rejectWithValue }) => {
    try {
      return await themeApi.deletePalette();
    } catch (err) {
      return rejectWithValue(extractError(err));
    }
  },
);

interface ThemeState {
  palette: BrandPalette | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: ThemeState = { palette: null, status: 'idle', error: null };

export const themeSlice = createSlice({
  name: 'theme',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTheme.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchTheme.fulfilled, (state, { payload }) => {
        state.status = 'succeeded';
        state.palette = payload;
      })
      .addCase(fetchTheme.rejected, (state, { payload }) => {
        state.status = 'failed';
        state.error = payload ?? null;
      })
      // Salvataggio: aggiorna SOLO la palette, non lo status — un transitorio
      // 'loading' farebbe ricadere il ThemeManager sulla palette in cache e il
      // tema sfarfallerebbe mentre si salva.
      .addCase(updateCompanyPalette.fulfilled, (state, { payload }) => {
        state.palette = payload;
      })
      // Ripristino: stessa ragione del salvataggio per non toccare lo status, e
      // scrivere in store la palette tornata dal client fa sì che ThemeManager la
      // rimetta in cache — senza, al reload riapparirebbe quella cancellata.
      .addCase(resetCompanyPalette.fulfilled, (state, { payload }) => {
        state.palette = payload;
      });
  },
});

export const themeReducer = themeSlice.reducer;
